import { configureRequestParams, getErrorMessage } from '@/utils/helpers'

import axios from './instance'

/**
 * Resolve the base path for a CourseAsset request.
 *
 * - With organizationId → /organizations/<organizationId>/course-assets/ (the
 *   org's own shared assets — RSCX-320)
 * - With entityId → /entities/<entityId>/course-assets/ (returns the entity's
 *   own facility assets + global RescueX + org-shared assets)
 * - With neither → /course-assets/ (RescueX-only)
 */
const basePath = (entityId, organizationId) => {
  if (organizationId) return `/organizations/${organizationId}/course-assets/`
  if (entityId) return `/entities/${entityId}/course-assets/`
  return '/course-assets/'
}

/**
 * List CourseAssets.
 *
 * @param {object} payload
 * @param {string} [payload.entityId]
 * @param {Array} [payload.sorting]
 * @param {object} [payload.pagination]
 * @param {string} [payload.searchTerm]
 * @param {object} [payload.filters]
 */
export const getCourseAssets = async (payload = {}) => {
  const { entityId, organizationId, sorting, pagination, searchTerm, filters } = payload
  const params = configureRequestParams(sorting, pagination, searchTerm, filters)
  try {
    return await axios.get(`${basePath(entityId, organizationId)}${params}`)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Fetch a single CourseAsset.
 *
 * @param {object} payload
 * @param {string} [payload.entityId]
 * @param {string} payload.id
 */
export const getCourseAsset = async ({ entityId, organizationId, id }) => {
  try {
    return await axios.get(`${basePath(entityId, organizationId)}${id}/`)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Create a CourseAsset. The backend binds entity + is_xverse_asset based on
 * the URL — callers don't pass them.
 *
 * @param {object} payload
 * @param {string} [payload.entityId]
 */
export const addCourseAsset = async ({ entityId, organizationId, ...data }) => {
  try {
    return await axios.post(basePath(entityId, organizationId), data)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Update a CourseAsset.
 *
 * @param {object} payload
 * @param {string} [payload.entityId]
 * @param {string} payload.id
 */
export const updateCourseAsset = async ({ entityId, organizationId, id, ...data }) => {
  try {
    return await axios.patch(`${basePath(entityId, organizationId)}${id}/`, data)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Delete a CourseAsset. Backend returns 409 (with the in-use course list) if
 * any CourseContent still references the asset.
 *
 * @param {object} payload
 * @param {string} [payload.entityId]
 * @param {string} payload.id
 */
export const deleteCourseAsset = async ({ entityId, organizationId, id }) => {
  try {
    return await axios.delete(`${basePath(entityId, organizationId)}${id}/`)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}
